import { CheckIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import type { CloudOnboardingStep, CloudOnboardingStepId } from "./CloudOnboarding.logic";

/** Action slots keyed by the step that owns them; the panel supplies the handlers. */
export type CloudOnboardingStepActions = Partial<Record<CloudOnboardingStepId, ReactNode>>;

function StepMarker({
  status,
  index,
}: {
  readonly status: CloudOnboardingStep["status"];
  readonly index: number;
}) {
  if (status === "done") {
    return (
      <span
        className="grid size-5.5 place-items-center rounded-full bg-success/16 text-success-foreground"
        aria-hidden
      >
        <CheckIcon className="size-3" />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "grid size-5.5 place-items-center rounded-full border text-[11px] font-semibold",
        status === "current"
          ? "border-primary bg-primary text-primary-foreground"
          : status === "blocked"
            ? "border-warning/40 bg-warning/8 text-warning-foreground"
            : "border-muted-foreground/35 text-muted-foreground",
      )}
      aria-hidden
    >
      {index + 1}
    </span>
  );
}

export function CloudOnboardingSteps({
  steps,
  actions,
}: {
  readonly steps: ReadonlyArray<CloudOnboardingStep>;
  readonly actions?: CloudOnboardingStepActions;
}) {
  return (
    <ol className="px-3 py-3 sm:px-4">
      {steps.map((step, index) => {
        const action = actions?.[step.id];
        const isLast = index === steps.length - 1;
        return (
          <li key={step.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <StepMarker status={step.status} index={index} />
              {isLast ? null : <div className="my-1 w-px flex-1 bg-border" />}
            </div>
            <div className={cn("min-w-0 flex-1", isLast ? "" : "pb-4")}>
              <p
                className={cn(
                  "text-sm font-medium tracking-[-0.005em]",
                  step.status === "upcoming" ? "text-muted-foreground" : "text-foreground",
                )}
              >
                {step.title}
              </p>
              <p
                className={cn(
                  "mt-0.5 max-w-xl text-[13px] leading-[1.45]",
                  step.status === "upcoming"
                    ? "text-muted-foreground/60"
                    : "text-muted-foreground/80",
                )}
              >
                {step.description}
              </p>
              {action ? <div className="mt-2.5">{action}</div> : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
